import { Accordion } from "react-bootstrap";

const Stubtracker = () => {
  return (
    <div className="container">
      <h1 className="center">Stubtracker</h1>
      <h5 className="center py-4">
        Stubtracker is a Chrome extension designed to let you see how many stubs
        you've made from flipping cards in The Show.
      </h5>
      <h2>Getting Started</h2>
      <ol>
        <li>
          Download the Chrome Extension onto your desktop or laptop by{" "}
          <a href="/downloads/stubtracker25.0.0.zip">clicking here</a>.
        </li>
        <li>
          Unzip the file. This will create a folder named `build` on your
          computer.
        </li>
        <li>
          Load the extension into Chrome by following these steps:
          <ol>
            <li>
              Open Google Chrome and navigate to{" "}
              <a href="chrome://extensions/" target="_new">
                chrome://extensions/
              </a>
            </li>
            <li>
              Enable Developer Mode by clicking the toggle switch next to
              Developer mode.
            </li>
            <li>
              Click the "Load unpacked" button and select the `build` folder
              that you just created.
            </li>
          </ol>
        </li>
        <li>
          Go to{" "}
          <a
            href="https://mlb25.theshow.com/orders/completed_orders"
            target="_new"
          >
            The Show's Community Market site
          </a>
          , and if you aren't already logged in, log in and connect your PSN or
          Xbox account.
        </li>
        <li>
          Once you see some of your completed orders, open the Stubtracker
          extension by clicking on its icon in Chrome's extension. You may have
          to click on the puzzle piece icon to see the extension.
        </li>
        <li>
          Inside the extension that pops up, click on the "Load Orders" tab.
        </li>
        <li>
          Click on the "Load Missing Data" button to begin loading data. It
          should start navigating through your order history and loading the
          data into the extension. If it will take more than 15 minutes, you'll
          see an estimate of how long it will take for your data to fully load.
          The first time you do this, you'll likely have a lot of history to
          load. But the next time, you'll only have to load in what happened
          since your last load, so you won't need to wait that long every time.
        </li>
        <li>
          When your data has finished loading, go to the "Active Cards" tab to
          see the cards you've been flipping most recently (in the past two
          days). For each card, you'll see a tally of buys and sells, as well as
          calculations of your total profit and stubs per hour.
        </li>
      </ol>
      <br />
      <h2>Video</h2>
      <p>
        If you'd like to see a walkthrough of how to set up Stubtracker, you can
        watch this video
        <br />
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/XS3BkWwKjq0"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </p>
      <h2>More Details</h2>
      <Accordion flush>
        <Accordion.Item eventKey="0">
          <Accordion.Header>The Load Orders Tab</Accordion.Header>
          <Accordion.Body>
            <p>
              If you are not currently on a "Completed Orders" page, this tab
              will prompt you to navigate to one so that it can load in your
              order history. If you are already on a "Completed Orders" page, it
              will load your order history from that page into the extension.
              And you can click the "Load Missing Data" button to load in any
              data that hasn't been loaded yet.
            </p>
            <p>
              You can always reset to a blank slate by choosing the "Clear All
              Data" button, which will wipe out the record of your old orders
              and archived cards.
            </p>
            <p>
              This data is stored locally on your computer, so if you switch to
              a different machine, your data won't come with you.
            </p>
            <p>
              If you see a number next to the "Load Orders" tab label, that's
              the number of pages you're missing.
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          <Accordion.Header>The Active Cards Tab</Accordion.Header>
          <Accordion.Body>
            <p>
              This tab shows you the cards you've been flipping most recently.
              It's a good way to see how you're doing in real time, as it only
              shows cards you've interacted with in the past two days.
            </p>
            <p>
              It's your snapshot view of how recent flipping is working out, in
              terms of profit and loss. For each card shown in this section, you
              can see a record of how many buy and sell orders you've completed,
              and the data range in which those transactions have occurred. For
              each card, you'll also see a calculation of how many total stubs
              you've made and how that breaks down to per-hour profit.
            </p>
            <p>
              You can click on the name of a card to see a graph of how
              profitable that card has been for you over time, along with its
              transaction history.
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>The Old Cards Tab</Accordion.Header>
          <Accordion.Body>
            <p>
              The Old Cards tab is the area of the app that shows you all the
              cards that you've flipped in the past, not just the last two days.
              It's your historical record of all cards you've bought or sold in
              this year's version of The Show. Like with the Active Cards
              section, for each card, you can see a count of buy/sell orders,
              your total profit on the card, and how that profit broke down to a
              per-hour rate.
            </p>
            <p>
              There will be cases where you'll want to remove a card from your
              Old Cards section by "archiving" that card. For example, cards
              that you only bought one copy of and never sold are likely cards
              you purchased to play with rather than to flip. For those orders,
              you'll see only one buy record, no sell record, and a negative hit
              on your stub profit total. When you "archive" a card, it falls off
              of the Old Cards tab and shows up in the Archived Cards section.
              There, it continues to be available for reference and can be
              "unarchived" at any time, which moves it from the Archived Cards
              tab to the Old Cards tab.
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="3">
          <Accordion.Header>The Archived Cards Tab</Accordion.Header>
          <Accordion.Body>
            <p>
              The Archived Cards tab is the area of the app that shows you
              historical cards that you've archived. Archived cards do not
              contribute to your overall profit figure for Stubtracker, so it's
              likely where you'll store cards you purchased to play with rather
              than to flip. By moving cards like that out of Old Orders and into
              Archived Orders, you'll get a more accurate idea of your actual
              profit figures.
            </p>
            <p>
              Cards that have exactly one buy order and no sell orders are
              automatically archived by default, but you can unarchive them from
              this tab.
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="4">
          <Accordion.Header>The Share Tab</Accordion.Header>
          <Accordion.Body>
            <p>
              The Share tab is the area of the app that lets you show off how
              many stubs you've made from flipping. If you're a Stubminer user,
              you can include your affiliate ID in the share tab's input field
              to include it in any profit figures you share from the app, and
              anyone who signs up using your affiliate ID will get a discount
              when joining and also generate a referral credit for you! Once
              you've added your affiliate code to the input on the Share tab,
              you can click the "Copy Sharable Text" button to copy a quick text
              description of your profit totals, or you can choose "Share on
              Twitter" to generate a pre-populated Tweet that shows off your
              profit and affiliate code.
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="5">
          <Accordion.Header>The Card Details Screen</Accordion.Header>
          <Accordion.Body>
            <p>
              From the Active Orders and Old Cards tabs, you can view a card's
              details page by selecting any card from the displayed list.
            </p>
            <p>
              On the card details page, you can see a graphical representation
              of how many stubs you've made from the card over time, represented
              in a line chart. Below that, you can see a ledger of each order
              for that card that's being included in profit calculations. For
              any row in that table, you can take two actions: viewing the page
              in your Completed Orders that the transaction is tied to, or
              ignoring the order, which removes it from profit calculations for
              that card.
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default Stubtracker;
